import React, { forwardRef } from "react";
import "../style/Station.css";
import { fetchPostRequest } from "./../config/authentication/AuthService";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ExportCsv, ExportPdf } from "@material-table/exporters";

import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinearProgress from "@mui/material/LinearProgress";
import MaterialTable from "@material-table/core";
import _ from "underscore";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => (
    <KeyboardArrowDownIcon {...props} ref={ref} />
  )),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const TableTheme = createTheme({
  palette: {
    primary: {
      main: "#009fda",
    },
    secondary: {
      main: "#009fda",
    },
  },
  overrides: {
    MuiLinearProgress: {
      root: {
        height: 10,
        borderRadius: 5,
      },
      colorPrimary: {
        backgroundColor: "#F7F8F9",
      },
      bar: {
        borderRadius: 5,
      },
    },
    MuiButtonBase: {
      root: {
        MuiTableSortLabel: {
          root: {
            hover: {
              color: "red",
            },
          },
        },
      },
    },
    MuiTableRow: {
      hover: {
        "&$hover:hover": {
          backgroundColor: "#ffffff",
        },
      },
    },
    MuiIconButton: {
      root: {
        color: "#3E3E3E !important",
      },
    },
    MuiTableCell: {
      body: {
        color: "#3E3E3E !important",
        backgroundColor: "#F7F8F9 !important",
      },
      head: {
        color: "#3E3E3E !important",
        backgroundColor: "#F7F8F9 !important",
      },
      stickyHeader: {
        color: "#3E3E3E !important",
        background: "#F7F8F9 !important",
      },
    },
    MuiToolbar: {
      root: {
        backgroundColor: "#F7F8F9 !important",
      },
    },
    MuiTableSortLabel: {
      active: {
        color: "#32908F !important",
      },
    },
    MuiInputBase: {
      input: {
        paddingLeft: "5px !important",
        paddingRight: "5px !important",
        color: "#3E3E3E !important",
      },
    },
    MuiChip: {
      root: {
        backgroundColor: "#32908F !important",
      },
    },
    MuiTypography: {
      caption: {
        color: "#3E3E3E !important",
      },
    },
    MuiSwitch: {
      switchBase: {
        color: "#44D74B !important",
        "&$checked": {
          color: "#44D74B !important",
        },
        "&$checked + $track": {
          backgroundColor: "#44D74B !important",
        },
      },
    },
  },
});

const columns = [
  {
    field: "session_id",
    title: "Session ID",
    type: "numeric",
    align: "left",
  },
  {
    field: "station_name",
    title: "Station Name",
    width: 220,
  },
  {
    field: "start_time",
    title: "Start",
    type: "datetime",
    filtering: false,
    width: 150,
  },
  {
    field: "end_time",
    title: "End",
    type: "datetime",
    filtering: false,
    width: 150,
  },
  {
    field: "user_id",
    title: "User",
    filtering: false,
  },
  {
    field: "sg_name",
    title: "Station Group",
  },
  {
    field: "energy",
    title: "Energy (kWh)",
    type: "numeric",
    filtering: false,
    align: "left",
  },
  {
    field: "tx_cost",
    title: "Cost",
    type: "currency",
    filtering: false,
    align: "left",
  },
];

const REQUEST_LIMIT = 10000;

class SessionContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sessions: [],
      loading: true,
      totalSessions: 1,
      offset: 0,
      categorySelected: "",
      showStationDetails: false,
      setSelectedRow: false,

      modalOpen: false,
    };
  }

  _fetchGetSessions = async (body) => {
    return fetchPostRequest(`/sessions/history`, body);
  };

  async componentDidMount() {
    try {
      Promise.all([
        this._fetchGetSessions({
          count: "",
          stationGroupIds: this.props.stationGroupIds,
        }),
      ]).then(([countResponse]) => {
        this.setState({ totalSessions: countResponse });
        // console.log(
        //   `Sessions.js - sessionStore to retrieve: ${JSON.stringify(
        //     countResponse
        //   )}`
        // );

        let requestCount = (countResponse / REQUEST_LIMIT).toFixed(0);
        if (countResponse.count % REQUEST_LIMIT !== 0) requestCount++;
        this._getAllSessions(requestCount);
      });
    } catch (e) {
      return e.message;
    }
  }

  _getAllSessions = async (requestCount) => {
    let sessionArray = [];
    let res;
    for (let counter = 0; counter < requestCount; counter++) {
      res = await this._fetchGetSessions({
        offset: counter * REQUEST_LIMIT,
        limit: REQUEST_LIMIT,
        stationGroupIds: this.props.stationGroupIds,
      });
      sessionArray = [...sessionArray, ...res];
      this.setState({
        offset: counter * REQUEST_LIMIT + REQUEST_LIMIT,
        sessions: sessionArray,
      });
      console.log(
        `Sessions.js - getAllSessions offset: ${counter * REQUEST_LIMIT}`
      );
    }
    this.setState({ loading: false });
  };

  mapDateObjectsToLocalDate = (sessions) => {
    return sessions.map(ses => {
      return {
      ...ses,
      end_time: new Date(ses.end_time).toLocaleString("en-US", {timeZone: 'EST'}),
      start_time: new Date(ses.start_time).toLocaleString("en-US", {timeZone: 'EST'}),
      }
    })
  }

  render() {
    let rows = this.state.sessions || [];

    return (
      <div
        style={{
          backgroundColor: "#f7f8f9",
          height: "100%",
          width: "100%",
          paddingTop: "40px",
          paddingLeft: "60px",
          paddingRight: "60px",
          paddingBottom: "40px",
        }}
      >
        <span
          style={{ color: "#404040", fontWeight: "bold", fontSize: "36px" }}
        >
          Charging Session History
        </span>
        <div className="row mr-1 ml-1 mt-3" style={{ display: "block" }}>
          <div className="col-12">
            {this.state.loading ? (
              <div className="row">
                <div
                  className="col-12"
                  style={{ justifyContent: "center", marginBottom: "1vh" }}
                >
                  <ThemeProvider theme={TableTheme}>
                    <LinearProgress
                      variant="determinate"
                      value={parseInt(
                        (this.state.offset / this.state.totalSessions) *
                        100
                      .toFixed(0))}
                    />
                  </ThemeProvider>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>

          <ThemeProvider theme={TableTheme}>
            <MaterialTable
              columns={columns}
              data={rows}
              title=""
              icons={tableIcons}
              options={{
                maxBodyHeight: "calc(65vh)",
                rowStyle: {
                  backgroundColor: "#FFF",
                  color: "#3E3E3E",
                  fontSize: "13px",
                  textAlign: "left",
                },
                headerStyle: {
                  backgroundColor: "#0073a8",
                  color: "#ffffff",
                  fontWeight: "bold",
                  position: "sticky",
                  height: 52,
                },
                searchFieldStyle: {
                  border: "#C4C8C7 solid 1px",
                  borderRadius: 4,
                  padding: "3px",
                  marginRight: 20,
                },
                exportAllData: true,
                exportButton: true,
                exportMenu: [
                  {
                    label: "Export PDF",
                    exportFunc: (cols, datas) => {
                      ExportPdf(cols, this.mapDateObjectsToLocalDate(datas), "SessionExportPDF");
                    },
                  },
                  {
                    label: "Export CSV",
                    exportFunc: (cols, datas) => {

                      ExportCsv(cols, this.mapDateObjectsToLocalDate(datas), "SessionExportCSV");
                    },
                  },
                ],
                pageSize: 10,
                pageSizeOptions: [10, 25, 50, 100],
                filtering: true,
                grouping: true,
                filterCellStyle: {
                  backgroundColor: "#F7F8F9",
                  color: "#3E3E3E",
                  fontSize: "11px",
                },
              }}
            />
          </ThemeProvider>
        </div>
      </div>
    );
  }
}

export default SessionContainer;
